/**
 * Format a timestamp into a human-readable relative time string
 * @param {string|Date} timestamp - The timestamp to format
 * @param {Function} t - Translation function from react-i18next
 * @returns {string} Formatted time string
 */
export const formatTimestamp = (timestamp, t) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return t('channels.justNow');
  if (diffMins < 60) return t('channels.minutesAgo', { count: diffMins });
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return t('channels.hoursAgo', { count: diffHours });
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t('channels.daysAgo', { count: diffDays });
  
  return date.toLocaleDateString();
};

/**
 * Get remaining time until user can post again
 * @param {string|Date|null} nextPostAt - The timestamp when user can post next
 * @param {Function} t - Translation function from react-i18next
 * @returns {string|null} Formatted time remaining string or null
 */
export const getTimeRemaining = (nextPostAt, t) => {
  if (!nextPostAt) return null;
  
  const now = new Date();
  const next = new Date(nextPostAt);
  const diffMs = next - now;
  
  if (diffMs <= 0) return null;
  
  const diffSecs = Math.ceil(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  
  // Show seconds if less than 1 minute
  if (diffMins < 1) {
    return t('channels.message.rateLimitSecond', { count: diffSecs });
  }
  
  if (diffMins < 60) {
    return t('channels.message.rateLimitMinute', { count: diffMins });
  }
  
  const diffHours = Math.floor(diffMins / 60);
  return t('channels.message.rateLimitHour', { count: diffHours });
};

/**
 * Format a timestamp for simple display (used in Channels.jsx)
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} Formatted time string
 */
export const formatTimestampSimple = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString();
};

/**
 * Get time remaining in simple format (used in Channels.jsx)
 * @param {string|Date|null} nextPostAt - The timestamp when user can post next
 * @returns {string} Formatted time remaining string
 */
export const getTimeRemainingSimple = (nextPostAt) => {
  if (!nextPostAt) return 'You can post now!';
  
  const now = new Date();
  const next = new Date(nextPostAt);
  const diffMs = next - now;
  
  if (diffMs <= 0) return 'You can post now!';
  
  const diffMins = Math.ceil(diffMs / 60000);
  if (diffMins < 60) return `${diffMins} minutes`;
  
  const diffHours = Math.ceil(diffMs / 3600000);
  return `${diffHours} hours`;
};
